"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import type { KeyCandidate, PitchFrame } from "../lib/music";
import { AndroidMidi, hasAndroidMidiBridge, type AndroidMidiDiagnostics, type AndroidMidiMessage, type AndroidMidiState } from "../lib/android-midi";
import {
  advanceVoicePitchFrame,
  EMPTY_VOICE_DISPLAY_STATE,
  parseMidiMessage,
  type NoteEvent,
  type ParsedMidiMessage,
  type VoiceDisplayState,
} from "../lib/note-events";
import {
  analyzeLiveKeyEvidence,
  buildKeyboardGuideState,
  clearLiveKeyEvidence,
  diatonicHarmonyNote,
  importCaptureKey,
  keyCandidateLabel,
  undoLiveKeyEvidence,
  type HarmonyGuideMode,
  type LiveKeyEvidence,
  type LiveKeySource,
} from "../lib/live-key-guide";
import { LiveKeyGuidePanel } from "./LiveKeyGuidePanel";
import { detectWebRuntimeCapabilities, webMidiUnavailableMessage } from "../lib/platform-capabilities";

type Props = {
  captureKeyCandidates: KeyCandidate[];
  liveKey: KeyCandidate | null;
  liveKeySource: LiveKeySource;
  keyGuideEnabled: boolean;
  harmonyMode: HarmonyGuideMode;
  onSelectLiveKey: (candidate: KeyCandidate | null, source: LiveKeySource) => void;
  onKeyGuideEnabled: (enabled: boolean) => void;
  onHarmonyMode: (mode: HarmonyGuideMode) => void;
  voiceFrame: PitchFrame | null;
  isVoiceListening: boolean;
  onStartVoice: () => void;
  onStopVoice: () => void;
  onNoteStart: (noteNumber: number, key: string) => Promise<AudioContextState | "unavailable">;
  onNoteStop: (key: string) => void;
};

type PianoRange = { label: string; low: number; high: number };
type KeyGeometry = { noteNumber: number; isBlack: boolean; left: number; width: number };
type MidiInputPort = {
  id: string;
  name?: string | null;
  state?: string;
  onmidimessage: ((event: { data?: Uint8Array }) => void) | null;
};
type MidiAccessLike = {
  inputs: Map<string, MidiInputPort>;
  onstatechange: (() => void) | null;
};
type MidiNavigator = Navigator & {
  requestMIDIAccess?: () => Promise<MidiAccessLike>;
};

const RANGES: PianoRange[] = [
  { label: "C2–C5 · 3 OCT", low: 36, high: 72 },
  { label: "C1–C6 · 5 OCT", low: 24, high: 84 },
  { label: "A0–C8 · 88 KEY", low: 21, high: 108 },
];
const BLACK_PITCH_CLASSES = new Set([1, 3, 6, 8, 10]);
const NOTE_NAMES = ["C", "C♯", "D", "D♯", "E", "F", "F♯", "G", "G♯", "A", "A♯", "B"];
const HARMONY_SHORT: Record<HarmonyGuideMode, string> = { off: "OFF", "third-below": "3↓", "third-above": "3↑", "sixth-below": "6↓", "sixth-above": "6↑" };

function noteName(noteNumber: number): string {
  return `${NOTE_NAMES[noteNumber % 12]}${Math.floor(noteNumber / 12) - 1}`;
}

function buildGeometry(low: number, high: number): { keys: KeyGeometry[]; whiteCount: number } {
  const notes = Array.from({ length: high - low + 1 }, (_, index) => low + index);
  const whiteCount = notes.filter((note) => !BLACK_PITCH_CLASSES.has(note % 12)).length;
  const whiteWidth = 100 / whiteCount;
  let whiteIndex = -1;
  const keys = notes.map((noteNumber) => {
    const isBlack = BLACK_PITCH_CLASSES.has(noteNumber % 12);
    if (!isBlack) {
      whiteIndex += 1;
      return { noteNumber, isBlack, left: whiteIndex * whiteWidth, width: whiteWidth };
    }
    return { noteNumber, isBlack, left: (whiteIndex + 0.68) * whiteWidth, width: whiteWidth * 0.64 };
  });
  return { keys, whiteCount };
}

export function PianoPositionMode({ captureKeyCandidates, liveKey, liveKeySource, keyGuideEnabled, harmonyMode, onSelectLiveKey, onKeyGuideEnabled, onHarmonyMode, voiceFrame, isVoiceListening, onStartVoice, onStopVoice, onNoteStart, onNoteStop }: Props) {
  const [rangeIndex, setRangeIndex] = useState(0);
  const [historySeconds, setHistorySeconds] = useState(5);
  const [stableVoiceNote, setStableVoiceNote] = useState<number | null>(null);
  const [activeVoiceEvent, setActiveVoiceEvent] = useState<NoteEvent | null>(null);
  const [events, setEvents] = useState<NoteEvent[]>([]);
  const [midiActiveNotes, setMidiActiveNotes] = useState<Map<number, NoteEvent>>(new Map());
  const [now, setNow] = useState(0);
  const [midiStatus, setMidiStatus] = useState("MIDIキーボードは未接続");
  const [midiBridgeState, setMidiBridgeState] = useState<AndroidMidiState | null>(null);
  const [midiAvailable, setMidiAvailable] = useState(false);
  const [screenActiveNotes, setScreenActiveNotes] = useState<Set<number>>(new Set());
  const [audioStatus, setAudioStatus] = useState<"ready" | AudioContextState | "unavailable">("ready");
  const voiceStateRef = useRef<VoiceDisplayState>(EMPTY_VOICE_DISPLAY_STATE);
  const [keyPanelOpen, setKeyPanelOpen] = useState(false);
  const [keyFinderOpen, setKeyFinderOpen] = useState(false);
  const [keyEvidence, setKeyEvidence] = useState<LiveKeyEvidence[]>([]);
  const activeVoiceRef = useRef<NoteEvent | null>(null);
  const midiActiveRef = useRef(new Map<number, NoteEvent>());
  const midiAccessRef = useRef<MidiAccessLike | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(112);
  const keyFinderOpenRef = useRef(false);

  const range = RANGES[rangeIndex];
  const geometry = useMemo(() => buildGeometry(range.low, range.high), [range]);
  const geometryByNote = useMemo(
    () => new Map(geometry.keys.map((key) => [key.noteNumber, key])),
    [geometry.keys],
  );

  const addKeyEvidence = useCallback((noteNumber: number, source: "screen" | "midi") => {
    if (!keyFinderOpenRef.current) return;
    setKeyEvidence((current) => [...current, {
      id: `key-evidence-${source}-${noteNumber}-${performance.now()}`,
      noteNumber,
      noteClass: ((noteNumber % 12) + 12) % 12,
      inputCount: 1,
      source,
    }].slice(-96));
  }, []);

  useEffect(() => {
    keyFinderOpenRef.current = keyFinderOpen;
  }, [keyFinderOpen]);

  const clearMidiNotes = useCallback(() => {
    const time = performance.now() / 1000;
    const active = midiActiveRef.current;
    if (active.size) {
      setEvents((current) => [...current, ...Array.from(active.values()).map((event) => ({ ...event, endTime: time }))].slice(-500));
    }
    active.clear();
    setMidiActiveNotes(new Map());
  }, []);

  const receiveMidiMessage = useCallback((parsed: Exclude<ParsedMidiMessage, null>, metadata?: { deviceId?: string; channel?: number }) => {
    const time = performance.now() / 1000;
    const active = midiActiveRef.current;
    if (parsed.kind === "on") {
      addKeyEvidence(parsed.noteNumber, "midi");
      const existing = active.get(parsed.noteNumber);
      if (existing) setEvents((current) => [...current, { ...existing, endTime: time }].slice(-500));
      active.set(parsed.noteNumber, {
        id: "midi-" + parsed.noteNumber + "-" + time,
        noteNumber: parsed.noteNumber,
        startTime: time,
        endTime: null,
        source: "midi",
        velocity: parsed.velocity,
        channel: metadata?.channel ?? parsed.channel,
        deviceId: metadata?.deviceId,
      });
    } else {
      const existing = active.get(parsed.noteNumber);
      if (existing) {
        setEvents((current) => [...current, { ...existing, endTime: time }].slice(-500));
        active.delete(parsed.noteNumber);
      }
    }
    setMidiActiveNotes(new Map(active));
  }, [addKeyEvidence]);

  const applyAndroidMidiState = useCallback((state: AndroidMidiState) => {
    setMidiBridgeState(state);
    if (!state.supported || state.state === "unsupported") {
      setMidiAvailable(false);
      setMidiStatus("この端末はMIDI入力に対応していません");
      clearMidiNotes();
      return;
    }
    setMidiAvailable(true);
    if (state.state === "error") {
      setMidiStatus("MIDI エラー · " + (state.error || "機器を確認してください"));
      clearMidiNotes();
    } else if (state.state === "connecting") {
      setMidiStatus("MIDI 接続中…");
    } else if (state.state === "connected") {
      const names = state.devices.filter((device) => device.connected).map((device) => device.name);
      setMidiStatus("MIDI ● " + (names.join(" / ") || "USB MIDI"));
    } else {
      setMidiStatus("MIDI 未接続");
      clearMidiNotes();
    }
  }, [clearMidiNotes]);

  useEffect(() => {
    let cancelled = false;
    const nativeHandles: { remove: () => Promise<void> }[] = [];
    const availabilityTimer = window.setTimeout(() => {
      if (hasAndroidMidiBridge()) {
        setMidiAvailable(true);
        setMidiStatus("MIDI 接続中…");
      } else {
        const capabilities = detectWebRuntimeCapabilities();
        setMidiAvailable(capabilities.webMidi);
        setMidiStatus(webMidiUnavailableMessage(capabilities));
      }
    }, 0);
    const timer = window.setInterval(() => setNow(performance.now() / 1000), 50);

    if (hasAndroidMidiBridge()) {
      void (async () => {
        try {
          const stateHandle = await AndroidMidi.addListener("midiState", applyAndroidMidiState);
          const diagnosticHandle = await AndroidMidi.addListener("midiDiagnostic", (diagnostics: AndroidMidiDiagnostics) => {
            setMidiBridgeState((current) => current ? { ...current, diagnostics } : current);
          });
          const messageHandle = await AndroidMidi.addListener("midiMessage", (message: AndroidMidiMessage) => {
            receiveMidiMessage(
              { kind: message.kind, noteNumber: message.noteNumber, velocity: message.velocity, channel: message.channel },
              { deviceId: message.deviceId, channel: message.channel },
            );
            void AndroidMidi.acknowledgeUiEvent({ kind: message.kind, noteNumber: message.noteNumber });
          });
          nativeHandles.push(stateHandle, diagnosticHandle, messageHandle);
          const state = await AndroidMidi.getStatus();
          if (!cancelled) applyAndroidMidiState(state);
        } catch (caught) {
          if (!cancelled) {
            console.error("[P-010 MIDI] Android bridge failed", caught);
            setMidiStatus("MIDI エラー · bridgeを開始できません");
            clearMidiNotes();
          }
        }
      })();
    }

    return () => {
      cancelled = true;
      window.clearTimeout(availabilityTimer);
      window.clearInterval(timer);
      nativeHandles.forEach((handle) => void handle.remove());
      clearMidiNotes();
    };
  }, [applyAndroidMidiState, clearMidiNotes, receiveMidiMessage]);

  useEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    const updateKeyboardHeight = () => {
      const available = board.getBoundingClientRect().height;
      if (available <= 0) return;
      const landscape = window.matchMedia("(orientation: landscape)").matches;
      const minimum = landscape ? 82 : 108;
      const maximum = landscape ? 126 : 180;
      const ratio = landscape ? 0.48 : 0.4;
      setKeyboardHeight(Math.round(Math.max(minimum, Math.min(maximum, available * ratio))));
    };
    const observer = new ResizeObserver(updateKeyboardHeight);
    observer.observe(board);
    updateKeyboardHeight();
    window.visualViewport?.addEventListener("resize", updateKeyboardHeight);
    window.addEventListener("orientationchange", updateKeyboardHeight);
    return () => {
      observer.disconnect();
      window.visualViewport?.removeEventListener("resize", updateKeyboardHeight);
      window.removeEventListener("orientationchange", updateKeyboardHeight);
    };
  }, []);

  useEffect(() => {
    voiceStateRef.current = advanceVoicePitchFrame(
      voiceStateRef.current,
      isVoiceListening ? voiceFrame : null,
      0.74,
      2,
      isVoiceListening ? 3 : 1,
    );
    setStableVoiceNote(voiceStateRef.current.stableNote);
  }, [isVoiceListening, voiceFrame]);

  useEffect(() => {
    const time = performance.now() / 1000;
    const active = activeVoiceRef.current;
    if (active?.noteNumber === stableVoiceNote) return;
    if (active) {
      setEvents((current) => [...current, { ...active, endTime: time }].slice(-500));
      activeVoiceRef.current = null;
      setActiveVoiceEvent(null);
    }
    if (stableVoiceNote !== null) {
      const nextVoiceEvent: NoteEvent = {
        id: `voice-${time}`,
        noteNumber: stableVoiceNote,
        startTime: time,
        endTime: null,
        source: "voice",
        confidence: voiceFrame?.confidence,
      };
      activeVoiceRef.current = nextVoiceEvent;
      setActiveVoiceEvent(nextVoiceEvent);
    }
  }, [stableVoiceNote, voiceFrame?.confidence]);

  function attachMidiInputs(access: MidiAccessLike) {
    const inputs = Array.from(access.inputs.values());
    for (const input of inputs) {
      input.onmidimessage = (event) => {
        if (!event.data) return;
        const parsed = parseMidiMessage(event.data);
        if (parsed) receiveMidiMessage(parsed);
      };
    }
    const connected = inputs.filter((input) => input.state !== "disconnected");
    if (!connected.length) clearMidiNotes();
    setMidiStatus(connected.length
      ? "MIDI ● " + connected.map((input) => input.name || "MIDI INPUT").join(" / ")
      : "MIDI入力が見つかりません");
  }

  async function connectMidi() {
    if (hasAndroidMidiBridge()) {
      setMidiStatus("MIDI 接続中…");
      try {
        applyAndroidMidiState(await AndroidMidi.refresh());
      } catch (caught) {
        console.error("[P-010 MIDI] refresh failed", caught);
        setMidiStatus("MIDI エラー · 再確認できません");
      }
      return;
    }
    const request = (navigator as MidiNavigator).requestMIDIAccess;
    if (!request) {
      setMidiStatus(webMidiUnavailableMessage(detectWebRuntimeCapabilities()));
      return;
    }
    try {
      const access = await request.call(navigator);
      midiAccessRef.current = access;
      access.onstatechange = () => attachMidiInputs(access);
      attachMidiInputs(access);
    } catch {
      setMidiStatus("MIDI接続が許可されませんでした");
    }

  }
  async function startScreenNote(event: ReactPointerEvent<HTMLButtonElement>, noteNumber: number) {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const key = `position-${event.pointerId}-${noteNumber}`;
    setScreenActiveNotes((current) => new Set(current).add(noteNumber));
    addKeyEvidence(noteNumber, "screen");
    try {
      setAudioStatus(await onNoteStart(noteNumber, key));
    } catch {
      setAudioStatus("unavailable");
      setScreenActiveNotes((current) => {
        const next = new Set(current);
        next.delete(noteNumber);
        return next;
      });
    }
  }

  function stopScreenNote(event: ReactPointerEvent<HTMLButtonElement>, noteNumber: number) {
    const key = `position-${event.pointerId}-${noteNumber}`;
    onNoteStop(key);
    setScreenActiveNotes((current) => {
      const next = new Set(current);
      next.delete(noteNumber);
      return next;
    });
  }

  const keyAnalysis = useMemo(() => analyzeLiveKeyEvidence(keyEvidence), [keyEvidence]);
  const currentMelodyNote = stableVoiceNote
    ?? Array.from(midiActiveNotes.keys()).at(-1)
    ?? Array.from(screenActiveNotes).at(-1)
    ?? null;
  const harmonyNote = keyGuideEnabled
    ? diatonicHarmonyNote(currentMelodyNote, liveKey, harmonyMode)
    : null;

  function chooseLiveKey(candidate: KeyCandidate, source: Exclude<LiveKeySource, "none">) {
    onSelectLiveKey(source === "capture" ? importCaptureKey(candidate) : { ...candidate }, source);
    onKeyGuideEnabled(true);
    setKeyFinderOpen(false);
    keyFinderOpenRef.current = false;
    setKeyPanelOpen(false);
  }

  function keyboardClassName(key: KeyGeometry): string {
    const state = buildKeyboardGuideState({
      noteNumber: key.noteNumber,
      key: liveKey,
      guideEnabled: keyGuideEnabled,
      harmonyNote,
      voiceActive: stableVoiceNote === key.noteNumber,
      midiActive: midiActiveNotes.has(key.noteNumber),
      screenActive: screenActiveNotes.has(key.noteNumber),
    });
    return [
      "piano-key",
      key.isBlack ? "black" : "white",
      state.scaleMember ? "scale-member" : "",
      state.tonic ? "tonic-key" : "",
      state.harmony ? "harmony-suggestion" : "",
      state.midiActive ? "midi-active" : "",
      state.voiceActive ? "voice-active" : "",
      state.screenActive ? "screen-active" : "",
      state.outsideScale ? "outside-scale" : "",
    ].filter(Boolean).join(" ");
  }
  const activeEvents = [
    ...(activeVoiceEvent ? [{ ...activeVoiceEvent, endTime: now }] : []),
    ...Array.from(midiActiveNotes.values()).map((event) => ({ ...event, endTime: now })),
  ];
  const visibleEvents = [...events, ...activeEvents].filter((event) => {
    const eventEnd = event.endTime ?? now;
    return eventEnd >= now - historySeconds && geometryByNote.has(event.noteNumber);
  });
  const voiceInRange = stableVoiceNote !== null && geometryByNote.has(stableVoiceNote);

  return (
    <section
      className="piano-position-mode"
      aria-label="LIVE PIANO リアルタイム鍵盤"
      style={{ "--piano-keyboard-height": keyboardHeight + "px" } as CSSProperties}
    >
      <div className="piano-mode-head">
        <div>
          <span>LIVE PIANO · REALTIME</span>
          <h2>いま鳴っている位置を、鍵盤で見る</h2>
          <p>声は青、MIDIキーボードはオレンジ。上の履歴は現在から過去へ流れます。</p>
        </div>
        <div className="piano-source-legend">
          <span className="voice-dot">VOICE</span>
          <span className="midi-dot">MIDI</span>
        </div>
      </div>

      <div className="piano-mode-controls">
        <button className={isVoiceListening ? "voice-listen active" : "voice-listen"} onClick={isVoiceListening ? onStopVoice : onStartVoice}>
          {isVoiceListening ? "■ VOICE停止" : "● VOICE開始"}
        </button>
        <button className="midi-connect" onClick={() => void connectMidi()} disabled={!midiAvailable}>
          MIDIを接続
        </button>
        <label>
          <span>RANGE</span>
          <select value={rangeIndex} onChange={(event) => setRangeIndex(Number(event.target.value))}>
            {RANGES.map((item, index) => <option value={index} key={item.label}>{item.label}</option>)}
          </select>
        </label>
        <label className="history-control">
          <span>HISTORY <strong>{historySeconds}s</strong></span>
          <input type="range" min="2" max="10" step="1" value={historySeconds} onChange={(event) => setHistorySeconds(Number(event.target.value))} />
        </label>
      </div>

      <div className="piano-device-status">
        <strong>{stableVoiceNote === null ? "VOICE —" : `VOICE ${noteName(stableVoiceNote)}`}</strong>
        <span>{voiceInRange || stableVoiceNote === null ? midiStatus : `${noteName(stableVoiceNote)} は表示範囲外 · RANGEを広げてください`}</span>
        <div className="piano-key-summary">
          <button type="button" className="key-summary-main" onClick={() => setKeyPanelOpen(true)}>
            <small>KEY · {liveKeySource.toUpperCase()}</small><strong>{keyCandidateLabel(liveKey)}</strong>
          </button>
          <button type="button" className={keyGuideEnabled ? "guide-toggle active" : "guide-toggle"} disabled={!liveKey} onClick={() => { const enabled = !keyGuideEnabled; onKeyGuideEnabled(enabled); if (!enabled) onHarmonyMode("off"); }}>
            GUIDE {keyGuideEnabled ? "ON" : "OFF"}
          </button>
          <button type="button" className={harmonyMode === "off" ? "harmony-summary" : "harmony-summary active"} disabled={!liveKey} onClick={() => setKeyPanelOpen(true)}>
            HARMONY {HARMONY_SHORT[harmonyMode]}
          </button>
        </div>
        <em>AUDIO {audioStatus === "ready" ? "鍵盤をタッチして開始" : audioStatus}</em>
        {midiBridgeState?.diagnostics && (
          <details className="midi-diagnostics">
            <summary>MIDI DIAG · {midiBridgeState.diagnostics.stage}</summary>
            <div className="midi-debug-strip" aria-label="Android MIDI経路診断">
            <b>API {midiBridgeState.diagnostics.midiFeature && midiBridgeState.diagnostics.midiManagerAvailable ? "SUPPORTED" : "UNSUPPORTED"}</b>
            <b>USB HOST {midiBridgeState.diagnostics.usbHost ? "YES" : "NO"} · USB {midiBridgeState.diagnostics.usbDeviceCount}</b>
            <b>DEVICES {midiBridgeState.diagnostics.midiDeviceCount}</b>
            <b>PORT {midiBridgeState.diagnostics.lastPort}</b>
            <b>RAW {midiBridgeState.diagnostics.lastRawMidi}</b>
            <b>EVENT {midiBridgeState.diagnostics.lastNoteEvent}</b>
            <strong>{midiBridgeState.diagnostics.stage}</strong>
            <small>{midiBridgeState.devices.map((device) => device.name + " · " + device.deviceType + " · " + device.ports.map((port) => port.type + port.number + (port.opened ? " OPEN" : "")).join(" / ")).join(" | ") || "MIDI deviceはまだ列挙されていません"}</small>
            </div>
          </details>
        )}
      </div>
      <LiveKeyGuidePanel
        open={keyPanelOpen}
        finderOpen={keyFinderOpen}
        selectedKey={liveKey}
        keySource={liveKeySource}
        guideEnabled={keyGuideEnabled}
        harmonyMode={harmonyMode}
        captureCandidates={captureKeyCandidates}
        evidence={keyEvidence}
        analysis={keyAnalysis}
        onClose={() => { keyFinderOpenRef.current = false; setKeyPanelOpen(false); setKeyFinderOpen(false); }}
        onOpenFinder={() => { keyFinderOpenRef.current = true; setKeyFinderOpen(true); }}
        onDoneFinder={() => { keyFinderOpenRef.current = false; setKeyFinderOpen(false); setKeyPanelOpen(false); }}
        onUndo={() => setKeyEvidence((current) => undoLiveKeyEvidence(current))}
        onClear={() => setKeyEvidence(clearLiveKeyEvidence())}
        onUseKey={chooseLiveKey}
        onOff={() => { onSelectLiveKey(null, "none"); onKeyGuideEnabled(false); onHarmonyMode("off"); }}
        onGuideChange={(enabled) => { onKeyGuideEnabled(enabled); if (!enabled) onHarmonyMode("off"); }}
        onHarmonyChange={onHarmonyMode}
      />
      <div className="piano-landscape-hint">iPhoneは横向きで使うと、鍵盤位置が最も見やすくなります</div>
      <div className="piano-board-scroll" ref={boardRef}>
        <div className="piano-board">
          <div className="note-history" aria-label={`直近${historySeconds}秒の音程履歴`}>
            <div className="history-time-label history-past">{historySeconds}s AGO</div>
            <div className="history-time-label history-now">NOW</div>
            {geometry.keys.filter((key) => !key.isBlack).map((key) => (
              <i key={`grid-${key.noteNumber}`} className="history-key-grid" style={{ left: `${key.left}%`, width: `${key.width}%` }} />
            ))}
            {visibleEvents.map((event) => {
              const key = geometryByNote.get(event.noteNumber);
              if (!key) return null;
              const end = event.endTime ?? now;
              const startAge = Math.max(0, now - event.startTime);
              const endAge = Math.max(0, now - end);
              const bottom = Math.min(100, (endAge / historySeconds) * 100);
              const height = Math.max(1.8, Math.min(100 - bottom, ((startAge - endAge) / historySeconds) * 100));
              const style = {
                left: `${key.left}%`,
                width: `${key.width}%`,
                bottom: `${bottom}%`,
                height: `${height}%`,
                "--note-velocity": event.velocity ? event.velocity / 127 : 0.72,
              } as CSSProperties;
              return <b key={event.id} className={`history-note ${event.source}`} style={style} title={`${event.source} ${noteName(event.noteNumber)}`} />;
            })}
          </div>

          <div className="piano-keyboard" aria-label={`${range.label} 鍵盤`}>
            {geometry.keys.filter((key) => !key.isBlack).map((key) => {
              return (
                <button
                  type="button"
                  key={key.noteNumber}
                  className={keyboardClassName(key)}
                  style={{ left: `${key.left}%`, width: `${key.width}%` }}
                  aria-label={noteName(key.noteNumber)}
                  onPointerDown={(event) => void startScreenNote(event, key.noteNumber)}
                  onPointerUp={(event) => stopScreenNote(event, key.noteNumber)}
                  onPointerCancel={(event) => stopScreenNote(event, key.noteNumber)}
                  onLostPointerCapture={(event) => stopScreenNote(event, key.noteNumber)}
                  onContextMenu={(event) => event.preventDefault()}
                >
                  {key.noteNumber % 12 === 0 && <span>{noteName(key.noteNumber)}</span>}
                </button>
              );
            })}
            {geometry.keys.filter((key) => key.isBlack).map((key) => {
              return (
                <button
                  type="button"
                  key={key.noteNumber}
                  className={keyboardClassName(key)}
                  style={{ left: `${key.left}%`, width: `${key.width}%` }}
                  aria-label={noteName(key.noteNumber)}
                  onPointerDown={(event) => void startScreenNote(event, key.noteNumber)}
                  onPointerUp={(event) => stopScreenNote(event, key.noteNumber)}
                  onPointerCancel={(event) => stopScreenNote(event, key.noteNumber)}
                  onLostPointerCapture={(event) => stopScreenNote(event, key.noteNumber)}
                  onContextMenu={(event) => event.preventDefault()}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
